import {
  collapseContext,
  diffLines,
  type DiffBlock,
  type DiffLine,
} from "./diff";

export interface TrackSpan {
  type: "same" | "ins" | "del";
  text: string;
}

export type TrackChangeBlock =
  | { type: "unchanged"; markdown: string }
  | {
      type: "revision";
      id: string;
      spans: TrackSpan[];
      summary: string;
      description: string;
    }
  | { type: "skip"; skipped: number };

function quoteSnippet(text: string): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t) return '""';
  if (t.length > 52) return `"${t.slice(0, 49)}…"`;
  return `"${t}"`;
}

export function suggestionDescription(spans: TrackSpan[]): string {
  const delText = spans
    .filter((s) => s.type === "del")
    .map((s) => s.text)
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  const insText = spans
    .filter((s) => s.type === "ins")
    .map((s) => s.text)
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  if (delText && insText)
    return `Replace ${quoteSnippet(delText)} with ${quoteSnippet(insText)}`;
  if (insText) return `Insert ${quoteSnippet(insText)}`;
  if (delText) return `Delete ${quoteSnippet(delText)}`;
  return "Suggested change";
}

/** Token LCS diff — tokens preserve whitespace when split with /(\s+)/. */
export function diffTokens(oldText: string, newText: string): TrackSpan[] {
  const a = oldText.match(/\S+|\s+/g) ?? (oldText ? [oldText] : []);
  const b = newText.match(/\S+|\s+/g) ?? (newText ? [newText] : []);
  const n = a.length;
  const m = b.length;
  const table: Uint32Array[] = [];
  for (let i = 0; i <= n; i++) table.push(new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i][j] =
        a[i] === b[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const out: TrackSpan[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: "same", text: a[i] });
      i++;
      j++;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      out.push({ type: "del", text: a[i] });
      i++;
    } else {
      out.push({ type: "ins", text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ type: "del", text: a[i++] });
  while (j < m) out.push({ type: "ins", text: b[j++] });
  return out;
}

function mergeAdjacentSpans(spans: TrackSpan[]): TrackSpan[] {
  const merged: TrackSpan[] = [];
  for (const span of spans) {
    const last = merged[merged.length - 1];
    if (last && last.type === span.type) last.text += span.text;
    else merged.push({ ...span });
  }
  return merged;
}

function revisionSummary(spans: TrackSpan[]): string {
  const hasIns = spans.some((s) => s.type === "ins");
  const hasDel = spans.some((s) => s.type === "del");
  if (hasIns && hasDel) return "Suggested edit";
  if (hasIns) return "Insertion";
  if (hasDel) return "Deletion";
  return "Change";
}

export function revisionMarkdown(spans: TrackSpan[]): string | null {
  if (
    !spans.every(
      (s) => s.type === "ins" || (s.type === "same" && /^\n+$/.test(s.text)),
    )
  ) {
    return null;
  }
  return spans.map((s) => s.text).join("");
}

function changeRunToRevision(run: DiffLine[], id: string): TrackChangeBlock {
  const spans: TrackSpan[] = [];
  let i = 0;
  while (i < run.length) {
    const line = run[i];
    if (
      line.type === "del" &&
      i + 1 < run.length &&
      run[i + 1].type === "add"
    ) {
      spans.push(...diffTokens(line.text, run[i + 1].text));
      if (i + 2 < run.length) spans.push({ type: "same", text: "\n" });
      i += 2;
      continue;
    }
    if (line.type === "del") {
      spans.push({ type: "del", text: line.text });
      if (i + 1 < run.length) spans.push({ type: "same", text: "\n" });
      i++;
      continue;
    }
    spans.push({ type: "ins", text: line.text });
    if (i + 1 < run.length) spans.push({ type: "same", text: "\n" });
    i++;
  }
  const merged = mergeAdjacentSpans(spans);
  return {
    type: "revision",
    id,
    spans: merged,
    summary: revisionSummary(merged),
    description: suggestionDescription(merged),
  };
}

function linesBlockToTrackBlocks(
  lines: DiffLine[],
  idPrefix: string,
  idCounter: { n: number },
): TrackChangeBlock[] {
  const blocks: TrackChangeBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].type === "same") {
      let j = i;
      while (j < lines.length && lines[j].type === "same") j++;
      blocks.push({
        type: "unchanged",
        markdown: lines
          .slice(i, j)
          .map((l) => l.text)
          .join("\n"),
      });
      i = j;
      continue;
    }
    let j = i;
    while (j < lines.length && lines[j].type !== "same") j++;
    idCounter.n += 1;
    blocks.push(
      changeRunToRevision(lines.slice(i, j), `${idPrefix}-${idCounter.n}`),
    );
    i = j;
  }
  return blocks;
}

/** Convert collapsed line diff into document-style track-change blocks. */
export function diffBlocksToTrackChanges(
  blocks: DiffBlock[],
  idPrefix = "s",
): TrackChangeBlock[] {
  const out: TrackChangeBlock[] = [];
  const idCounter = { n: 0 };
  for (const block of blocks) {
    if (block.type === "skip") {
      out.push({ type: "skip", skipped: block.skipped ?? 0 });
      continue;
    }
    out.push(...linesBlockToTrackBlocks(block.lines, idPrefix, idCounter));
  }
  return out;
}

export function buildTrackChanges(
  oldText: string,
  newText: string,
): TrackChangeBlock[] {
  return diffBlocksToTrackChanges(collapseContext(diffLines(oldText, newText)));
}

export function hasTrackChanges(blocks: TrackChangeBlock[]): boolean {
  return blocks.some((b) => b.type === "revision");
}

/**
 * Stable, content-derived key for a revision's persisted review state.
 *
 * The diff ids (`s-1`, `s-2`, …) are minted from a per-diff counter and
 * renumber whenever the diff changes, so they are transient view keys only.
 * This key hashes the del/ins content instead (FNV-1a), making it
 * deterministic for a given change regardless of diff order. Identical
 * changes at different positions intentionally share a key.
 */
export function suggestionKey(spans: TrackSpan[]): string {
  const del = spans
    .filter((s) => s.type === "del")
    .map((s) => s.text)
    .join("");
  const ins = spans
    .filter((s) => s.type === "ins")
    .map((s) => s.text)
    .join("");
  const input = `d:${del}\u0000i:${ins}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `sg-${h.toString(16).padStart(8, "0")}`;
}

export function listRevisions(blocks: TrackChangeBlock[]) {
  return blocks.filter(
    (b): b is Extract<TrackChangeBlock, { type: "revision" }> =>
      b.type === "revision",
  );
}

/**
 * Deterministic plain-text change log between two markdown bodies — one
 * `- ` bullet per change block, derived from suggestionDescription.
 */
export function changeLogSummary(oldText: string, newText: string): string {
  const revisions = listRevisions(buildTrackChanges(oldText, newText));
  if (revisions.length === 0) return "- no content changes";
  return revisions.map((r) => `- ${r.description}`).join("\n");
}

/** Apply a word-level replace suggestion back into markdown (best-effort). */
export function applySuggestionToMarkdown(
  markdown: string,
  spans: TrackSpan[],
): string {
  const delText = spans
    .filter((s) => s.type === "del")
    .map((s) => s.text)
    .join("");
  const insText = spans
    .filter((s) => s.type === "ins")
    .map((s) => s.text)
    .join("");
  if (delText && insText && markdown.includes(delText)) {
    return markdown.replace(delText, insText);
  }
  if (!delText && insText && !markdown.includes(insText.trim())) {
    return `${markdown.trimEnd()}\n\n${insText.trim()}\n`;
  }
  if (delText && !insText && markdown.includes(delText)) {
    return markdown.replace(delText, "");
  }
  return markdown;
}
